import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { INewFolder } from '@commenda/types/src/drive-view/types';
import { FileBasic } from '@commenda/types/src/file/types';
import { TWorkRequest } from '@commenda/types/src/work-request/types';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { ReactNode, useState } from 'react';
import { PreviewItemModal } from '../../components/PreviewModal';
import DocumentFolderView from '../../document-folder-view/document-folder-view';
import { filterValidFiles } from '../../document-views-v2/utils';
import { AddDocumentModal } from './AddDocumentModal';

interface DocumentsDriveViewProps {
  /** The ID of the business entity whose documents are being displayed */
  businessEntityId: number;

  /** The folder structure containing all documents */
  driveView: INewFolder;

  /** Whether the entity is currently being incorporated */
  isIncorporationInProgress?: boolean;

  /** Work requests for the incorporation alert */
  workRequests?: TWorkRequest[];

  /** Custom class name for the DocumentFolderView wrapper */
  className?: string;

  /**
   * Custom header content to render above the documents view.
   * Receives `onAddDocument` callback and `isAddButtonEnabled` to help render custom buttons.
   */
  renderHeader?: (props: {
    onAddDocument: () => void;
    isAddButtonEnabled: boolean;
  }) => ReactNode;

  /**
   * Whether to show the default "Add Document" button.
   * Only shown if `renderHeader` is not provided.
   * @default true (but respects feature flag and incorporation status)
   */
  showAddButton?: boolean;
}

/**
 * A reusable component for displaying and managing documents for a business entity.
 *
 * Features:
 * - Document folder navigation with breadcrumbs
 * - File preview modal
 * - Add document modal
 * - Incorporation in progress alert
 * - Automatic filtering of files with undefined IDs
 *
 * @example
 * // Basic usage
 * <DocumentsDriveView
 *   businessEntityId={123}
 *   driveView={driveViewData}
 * />
 *
 * @example
 * // With custom header
 * <DocumentsDriveView
 *   businessEntityId={123}
 *   driveView={driveViewData}
 *   isIncorporationInProgress={false}
 *   workRequests={workRequests}
 *   renderHeader={({ onAddDocument, isAddButtonEnabled }) => (
 *     <div className="flex justify-between items-center">
 *       <h1>Documents</h1>
 *       {isAddButtonEnabled && (
 *         <Button onClick={onAddDocument}>Add Document</Button>
 *       )}
 *     </div>
 *   )}
 * />
 */
export function DocumentsDriveView({
  businessEntityId,
  driveView,
  isIncorporationInProgress = false,
  workRequests = [],
  className = 'border-y',
  renderHeader,
  showAddButton = true,
}: DocumentsDriveViewProps) {
  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string>('root');
  const [currentFolderName, setCurrentFolderName] =
    useState<string>('Documents');

  // File preview state
  const [previewFile, setPreviewFile] = useState<FileBasic | null>(null);
  const previewModalVisibility = useToggle(false);

  // Add document modal state
  const addDocumentModalVisibility = useToggle(false);

  // Filter out files with undefined IDs
  const filteredRootFolder = filterValidFiles(driveView);

  // Determine if the add button should be enabled
  const isAddButtonEnabled =
    !isIncorporationInProgress &&
    isFeatureLaunched(FeatureLaunch.ADD_DOCUMENTS_BUTTON);

  const handleAddDocument = () => {
    addDocumentModalVisibility.show();
  };

  const handlePreviewFile = (file: FileBasic) => {
    mixpanel.track(MIXPANEL_EVENTS.PREVIEW_DOCUMENT, {
      surface_area: MIXPANEL_SURFACE_AREA.DOCUMENTS_TAB,
    });
    setPreviewFile(file);
    previewModalVisibility.show();
  };

  const handleFolderChange = (folderId: string, folderName: string) => {
    setCurrentFolderId(folderId);
    setCurrentFolderName(folderName);
  };

  const handleAddDocumentSuccess = (
    activeFolderId: string,
    activeFolderName: string
  ) => {
    mixpanel.track(MIXPANEL_EVENTS.ADDED_DOCUMENT, {
      surface_area: MIXPANEL_SURFACE_AREA.DOCUMENTS_TAB,
    });
    setCurrentFolderId(activeFolderId);
    setCurrentFolderName(activeFolderName);
  };

  const downloadFile = async (fileId: number) => {
    mixpanel.track(MIXPANEL_EVENTS.DOWNLOADED_DOCUMENT, {
      surface_area: MIXPANEL_SURFACE_AREA.DOCUMENTS_TAB,
    });
    const signedUrl = await getSignedUrlByFileId(fileId, undefined);
    return signedUrl;
  };

  return (
    <>
      {/* Preview Modal */}
      <PreviewItemModal
        isOpen={previewModalVisibility.visible}
        onClose={previewModalVisibility.hide}
        previewFile={previewFile}
      />

      {/* Add Document Modal */}
      <AddDocumentModal
        isOpen={addDocumentModalVisibility.visible}
        onClose={addDocumentModalVisibility.hide}
        businessEntityId={businessEntityId}
        onSuccess={handleAddDocumentSuccess}
      />

      {/* Custom header or default add button */}
      {renderHeader ? (
        renderHeader({
          onAddDocument: handleAddDocument,
          isAddButtonEnabled,
        })
      ) : showAddButton && isAddButtonEnabled ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={handleAddDocument}>
            <Icons.Plus className="size-4" />
            <span>Add Document</span>
          </Button>
        </div>
      ) : null}

      {/* Document Folder View */}
      <DocumentFolderView
        defaultOpenFolderId={currentFolderId}
        defaultOpenFolderName={currentFolderName}
        rootFolder={filteredRootFolder}
        downloadFile={downloadFile}
        onPreviewFile={handlePreviewFile}
        onFolderChange={handleFolderChange}
        className={className}
      />
    </>
  );
}
